import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators, ValidatorFn } from '@angular/forms';
import { DataService } from '../../../services/data/data.service';
import { Instituciones } from '../../../shared/catalogos';
import { firstValueFrom, map } from 'rxjs';
import Swal from 'sweetalert2';
import { messages } from '../../funciones/mensajes';
import { ResponsablesPlanesService } from '../../../services/Administrador/responsables-planes/responsables-planes.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-responsables-planes',
  templateUrl: './responsables-planes.component.html',
  styleUrl: './responsables-planes.component.css'
})
export class ResponsablesPlanesComponent implements OnInit {
  mostrarModal = false;
  mostrarDropdownInstitucion = false;
  instituciones = Instituciones;
  isAdministrador = false;
  responsables: any[] = [];
  editandoId: string | null = null;
  
  formResponsables = new FormGroup({
    nombre: new FormControl('', [Validators.required]),
    apellidoPaterno: new FormControl('', [Validators.required]),
    apellidoMaterno: new FormControl(''),
    correo: new FormControl('', [Validators.required, Validators.email]),
    puesto: new FormControl('', [Validators.required]),
    institucion: new FormControl('', [Validators.required])
  },{ validators: this.puestoDuplicidad() });

  constructor(
    public dataService: DataService,
    private responsablesService: ResponsablesPlanesService,
    private translateService: TranslateService
  ) { }

  ngOnInit() {
    this.verificarRol();
    this.cargarResponsables();
  }


  private puestoDuplicidad(): ValidatorFn {
    return (formGroup: FormGroup) => {
      var nombre = formGroup.get('puesto').value;
      if(!nombre) {return null;}
      nombre = nombre.trim().toLowerCase()
      const existe = this.responsables.some( el => {
        const exName = el.role_name.trim().toLowerCase();
        if(this.editandoId){
          return ( exName === nombre && el.id !== this.editandoId);
        }else{
          return exName === nombre;
        }  
      });
      if (existe) return { isValid: true};      
      return null;
    };
  }
  getInstitucionNombre(): string {
    const institucionId = this.formResponsables.get('institucion')?.value;
    const institucion = this.instituciones.find(inst => inst.id === institucionId);
    return institucion ? institucion.name : 'Sin seleccionar';
  }

  toggleDropdown() {
    this.mostrarDropdownInstitucion = !this.mostrarDropdownInstitucion;
  }

  seleccionarInstitucion(institutionId: any) {
    this.formResponsables.get('institucion')?.setValue(institutionId);
    this.mostrarDropdownInstitucion = false;
    this.cargarResponsables(); // Recargar responsables con la nueva institución
  }

  async cargarResponsables() {
    try {
      // Si es administrador, usa la institución seleccionada en el dropdown
      const institutionId = this.isAdministrador ? this.formResponsables.get('institucion')?.value : this.dataService.idInstitution;
      
      const resp = await firstValueFrom(
        this.responsablesService.getCatalogos(institutionId)
      );
      this.responsables = (resp.data?.rolesByInstitution || []).map(r => ({
        ...r
      }));
    } catch (error) {
      this.responsables = [];
    }
  }



  verificarRol() {
    this.isAdministrador = this.isAdministrador = /^(?:administrador|.*_administrador)(?:_|$)/i.test(
      this.dataService.role
    );
    
    // Si es institución, asignar automáticamente su ID
    if (!this.isAdministrador) {
      this.formResponsables.controls.institucion.setValue(this.dataService.idInstitution);
    }else if (this.instituciones.length > 0) {
      this.formResponsables.controls.institucion.setValue(this.instituciones[0].id);
    }
  }

  abrirModal() {
    this.mostrarModal = true;
    this.limpiarFormulario(true);
  }

  cerrarModal() {
    this.mostrarModal = false;
    this.limpiarFormulario(true);
  }

  async guardar() {
    if (this.formResponsables.valid) {
      const datosResponsable = {
        name: this.formResponsables.get('nombre')?.value,
        last_name: this.formResponsables.get('apellidoPaterno')?.value,
        last_name2: this.formResponsables.get('apellidoMaterno')?.value,
        email: this.formResponsables.get('correo')?.value,
        role_name: this.formResponsables.get('puesto')?.value,
        institution_id: this.formResponsables.get('institucion')?.value
      };
      
      try {
        if (this.editandoId) {
          // Editar responsable existente
          const datosActualizar = { ...datosResponsable, id: this.editandoId };
          messages.messageSaving(
            this.translateService.instant('responsables-planes.actualizando'),
            this.translateService.instant('responsables-planes.actualizando-desc')
          );
          await firstValueFrom(this.responsablesService.updateCatalogo(datosActualizar));
          messages.succesMessageGraph(this.translateService.instant('responsables-planes.exito-actualizar'));
          const resp = this.responsables.find(res => res.id === this.editandoId);
          resp.name = datosActualizar.name;
          resp.last_name = datosActualizar.last_name;
          resp.last_name2 = datosActualizar.last_name2;
          resp.email = datosActualizar.email;
          resp.role_name = datosActualizar.role_name;
          resp.institution_id = datosActualizar.institution_id;
        } else {
          // Crear nuevo responsable
          messages.messageSaving(
            this.translateService.instant('responsables-planes.creando'),
            this.translateService.instant('responsables-planes.creando-desc')
          );
          const responsable = await firstValueFrom(this.responsablesService.postCatalogo(datosResponsable));
          const res = {...responsable.data}
          this.responsables = this.responsables.concat(res);
          messages.succesMessageGraph(this.translateService.instant('responsables-planes.exito-crear'));
        }
        
        this.cerrarModal();
      } catch (error) {
        messages.errorMessageGraph(this.translateService.instant('responsables-planes.error-guardar') + ': ' + error);
      }
    }
  }

  validarFormulario(): boolean {
    return this.formResponsables.valid;
  }

  onBlur(campo: string) {
    // La validación de FormGroup es automática
  }

  onFocus(campo: string) {
    // La validación de FormGroup es automática
  }

  limpiarFormulario(mantenerInstitucion: boolean = false) {
    if (mantenerInstitucion) {
      const institucionActual = this.formResponsables.get('institucion')?.value;
      this.formResponsables.reset({
        institucion: institucionActual
      });
    } else {
      this.formResponsables.reset();
    }
    this.editandoId = null;
  }

  editarResponsable(responsable: any) {
    this.editandoId = responsable.id;
    
    // Llenar el formulario con los datos del responsable
    this.formResponsables.patchValue({
      nombre: responsable.name || '',
      apellidoPaterno: responsable.last_name || '',
      apellidoMaterno: responsable.last_name2 || '',
      correo: responsable.email || '',
      puesto: responsable.role_name || '',
      institucion: responsable.institution_id || this.formResponsables.get('institucion')?.value || ''
    });
    
    // Marcar como touched para que la validación sea visible
    Object.keys(this.formResponsables.controls).forEach(key => {
      this.formResponsables.get(key)?.markAsTouched();
    });
    
    this.mostrarModal = true;
  }

  async eliminarResponsable(id: string) {
    Swal.fire({
      title: this.translateService.instant('responsables-planes.confirmar-eliminar'),
      text: this.translateService.instant('responsables-planes.accion-no-deshace'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#757575',
      confirmButtonText: this.translateService.instant('responsables-planes.confirmar-si'),
      cancelButtonText: this.translateService.instant('menu.cancel')
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await firstValueFrom(this.responsablesService.deleteCatalogo(id));
          this.responsables = this.responsables.filter(responsable => responsable.id !== id);
          
          messages.succesMessageGraph(this.translateService.instant('responsables-planes.exito-eliminar'));
        } catch (error) {
          messages.errorMessageGraph(this.translateService.instant('responsables-planes.error-eliminar') + ': ' + error);
        }
      }
    });
  }


}